/* The permission set of a Role or of one person's Custom access (Team
   Simplified 1a): one switch per product area and one per Tool, then Advanced
   with the single permissions, View only levels for the CRM, and the sensitive
   account controls. */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmLevel, CrmPermission } from '../../../api/client';
import { Icon, Seg } from '../../ChatAssistant/compact/kit';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { Check, CrmPlatformHints } from './AccessRow';
import { crmOfItem, grantedCrm, holdsCrm, levelOf, setLevel, type Access } from './permissionModel';
import { AREAS, SENSITIVE, areaCount, itemOn, toggleArea, toggleItem, type RoleArea, type RoleItem } from './roleModel';

function Switch({ on }: { on: boolean }) {
  return (
    <span className="ts-switch" style={{ background: on ? C.purple : C.toggleOff }}>
      <span style={{ transform: on ? 'translateX(12px)' : 'none' }} />
    </span>
  );
}

export function PermissionEditor({ access, onChange, disabled, inset }: {
  access: Access; onChange: (next: Access) => void; disabled?: boolean;
  /** Cards on the darker input background (inside the Custom box). */
  inset?: boolean;
}) {
  const { t } = useTranslation('agency');
  const [open, setOpen] = useState(false);
  const set = (next: Access) => { if (!disabled) onChange(next); };
  const sensitiveOn = SENSITIVE.filter((i) => itemOn(access, i)).length;
  const crm = grantedCrm(access);
  const viewOnly = crm.filter((p) => levelOf(access, p) === 'view').length;
  const notes = [t('ts.advancedNote'), sensitiveOn ? t('ts.sensitiveOnNote', { count: sensitiveOn }) : null, viewOnly ? t('ts.viewOnlyNote', { count: viewOnly }) : null].filter(Boolean).join(' · ');
  const cardBg = inset ? C.card : C.input;
  const levelOpts = [
    { id: 'full' as CrmLevel, label: t('fleet.level.full'), title: t('fleet.level.fullTip') },
    { id: 'view' as CrmLevel, label: t('fleet.level.view'), title: t('fleet.level.viewTip') },
  ];
  const sensitiveRow = (item: RoleItem & { whyKey: string }) => {
    const on = itemOn(access, item);
    return (
      <button key={item.id} type="button" disabled={disabled} onClick={() => set(toggleItem(access, null, item))}
        className="ts-danger-row" style={{ background: on ? 'color-mix(in srgb, var(--om-accent) 8%, transparent)' : 'transparent' }}>
        <span className="tm-ellipsis ts-danger-label">{t(item.labelKey)}</span>
        <span className="tm-ellipsis ts-danger-why">{t(item.whyKey)}</span>
        <Switch on={on} />
      </button>
    );
  };

  // Media download means nothing without a CRM permission (CONTEXT.md).
  const blocked = (item: RoleItem) => Boolean(item.needsCrm) && !holdsCrm(access.has);
  const button = (id: string, icon: string, label: string, hint: string, on: boolean, onClick: () => void, off = false) => (
    <button key={id} type="button" disabled={disabled || off} onClick={onClick}
      className="ts-area cpt-hover-border" style={{ background: cardBg, opacity: off ? 0.55 : undefined, cursor: off ? 'not-allowed' : undefined }}>
      <span className="ts-area-icon" style={{ background: on ? C.tint16 : 'var(--om-legacy-hover)', color: on ? C.purpleLt : C.muted }}>
        <Icon name={icon} size={14} />
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ font: f(600, 12.5), color: C.text }}>{label}</span>
        <span className="tm-ellipsis" style={{ font: f(400, 11.5), color: C.muted }}>{hint}</span>
      </span>
      <Switch on={on} />
    </button>
  );
  const areaButton = (area: RoleArea) => {
    const n = areaCount(access, area);
    const total = area.items.length;
    return button(area.id, area.icon, t(area.labelKey), n && n < total ? t('ts.areaPartial', { k: n, n: total }) : t(area.hintKey),
      n > 0, () => set(toggleArea(access, area)));
  };
  // A split area (Tools): each item is its own right, so its own button.
  const itemButtons = (area: RoleArea) => area.items.map((item) =>
    button(item.id, item.icon ?? area.icon, t(item.labelKey),
      blocked(item) ? t('ts.needsCrm') : item.hintKey ? t(item.hintKey) : '',
      itemOn(access, item), () => set(toggleItem(access, area, item)), blocked(item)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, opacity: disabled ? 0.75 : 1 }}>
      <div className="ts-areas">
        {AREAS.map((area) => (area.split ? itemButtons(area) : areaButton(area)))}
      </div>

      <button type="button" onClick={() => setOpen(!open)} className="ts-adv-toggle">
        <Icon name="chevron-right" size={13} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
        {t('fleet.advanced')}
        <span style={{ font: f(400, 11.5), color: C.muted }}>{notes}</span>
      </button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="ts-groups">
            {AREAS.map((area) => {
              const n = areaCount(access, area);
              return (
                <div key={area.id} style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                  <button type="button" disabled={disabled || area.items.every(blocked)} onClick={() => set(n === area.items.length ? toggleArea(access, area) : allOn(access, area))}
                    title={area.items.every(blocked) ? t('ts.needsCrm') : undefined}
                    className="tm-check" style={{ font: f(600, 12.5), color: C.text, height: 26, opacity: area.items.every(blocked) ? 0.55 : undefined }}>
                    <Check state={n === 0 ? 'none' : n === area.items.length ? 'all' : 'some'} />
                    <span style={{ flex: 1 }}>{t(area.labelKey)}</span>
                    <span style={{ font: f(400, 11), color: C.muted }}>{n}/{area.items.length}</span>
                  </button>
                  {area.items.length > 1 && area.items.map((item) => {
                    const crm = crmOfItem(item.id);
                    const check = (
                      <button key={item.id} type="button" disabled={disabled} onClick={() => set(toggleItem(access, area, item))}
                        className="tm-check" style={{ paddingLeft: 14, font: f(400, 12.5), color: C.body, minHeight: 24 }}>
                        <Check state={itemOn(access, item) ? 'all' : 'none'} />{t(item.labelKey)}
                      </button>
                    );
                    return crm ? <div key={item.id} className="tm-crm-row">{check}<CrmPlatformHints permission={crm} /></div> : check;
                  })}
                </div>
              );
            })}
          </div>

          {crm.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <Icon name="eye" size={13} color={C.purplePale} />
                <span style={{ font: f(600, 12.5), color: C.text }}>{t('fleet.levelsTitle')}</span>
                <span style={{ font: f(400, 11.5), color: C.muted }}>{t('fleet.levelsHint')}</span>
              </div>
              <div className="ts-levels">
                {crm.map((p: CrmPermission) => (
                  <div key={p} className="tm-level-row">
                    <span style={{ flex: 1, minWidth: 0, font: f(400, 12.5), color: C.body }}>{t(`fleet.crm.${p}`)}</span>
                    <Seg<CrmLevel> size="sm" disabled={disabled} value={levelOf(access, p)} onChange={(l) => set(setLevel(access, p, l))} options={levelOpts} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <Icon name="alert-triangle" size={13} color={C.purplePale} />
              <span style={{ font: f(600, 12.5), color: C.text }}>{t('ts.sensitiveTitle')}</span>
              <span style={{ font: f(400, 11.5), color: C.muted }}>{t('ts.sensitiveHint')}</span>
              <div style={{ flex: 1 }} />
              <span style={{ font: f(500, 11.5), color: sensitiveOn ? C.purplePale : C.muted }}>
                {sensitiveOn ? t('ts.nOn', { count: sensitiveOn }) : t('ts.allOff')}
              </span>
            </div>
            <div className="ts-danger" style={{ background: cardBg }}>{SENSITIVE.map(sensitiveRow)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Advanced group checkbox: a partly-on group fills up, a full one empties. */
function allOn(access: Access, area: (typeof AREAS)[number]): Access {
  const off = areaCount(access, area) ? toggleArea(access, area) : access;
  return toggleArea(off, area);
}
